/**
 * Cookie consent helpers (Law 506/2004) — choice + audit evidence for DPO review.
 */

import {
  COOKIE_POLICY_VERSION,
  LEGAL_POLICY_VERSION,
} from '../pages/legal/legalConfig';

export const CONSENT_STORAGE_KEY = 'cybertracer_cookie_consent';
export const CONSENT_EVIDENCE_KEY = 'cybertracer_cookie_consent_evidence';
export const CONSENT_NECESSARY = 'necessary';
export const CONSENT_ALL = 'all';

/**
 * @typedef {{
 *   choice: 'necessary'|'all',
 *   ts: string,
 *   privacyPolicyVersion: string,
 *   cookiePolicyVersion: string,
 *   source: string,
 *   userAgent?: string,
 * }} ConsentEvidence
 */

/**
 * @returns {'necessary'|'all'|null}
 */
export function getStoredConsent() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const v = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (v === CONSENT_NECESSARY || v === CONSENT_ALL) return v;
    const evidence = getConsentEvidence();
    if (evidence?.choice === CONSENT_NECESSARY || evidence?.choice === CONSENT_ALL) {
      return evidence.choice;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * @returns {ConsentEvidence|null}
 */
export function getConsentEvidence() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_EVIDENCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (parsed.choice !== CONSENT_NECESSARY && parsed.choice !== CONSENT_ALL)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param {'necessary'|'all'} choice
 * @param {string} [source]
 * @returns {ConsentEvidence}
 */
export function buildConsentEvidence(choice, source = 'banner') {
  const ua =
    typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 180) : '';
  return {
    choice,
    ts: new Date().toISOString(),
    privacyPolicyVersion: LEGAL_POLICY_VERSION,
    cookiePolicyVersion: COOKIE_POLICY_VERSION,
    source: String(source || 'banner'),
    userAgent: ua || undefined,
  };
}

/**
 * @param {'necessary'|'all'} value
 * @param {string} [source]
 */
export function setStoredConsent(value, source = 'api') {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (value !== CONSENT_NECESSARY && value !== CONSENT_ALL) return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
    window.localStorage.setItem(
      CONSENT_EVIDENCE_KEY,
      JSON.stringify(buildConsentEvidence(value, source)),
    );
  } catch {
    /* ignore */
  }
}

/**
 * @returns {string}
 */
export function resolveGtmId() {
  const raw =
    (typeof process !== 'undefined' &&
      process.env &&
      (process.env.REACT_APP_GTM_ID ||
        process.env.REACT_APP_VITE_GTM_ID ||
        process.env.VITE_GTM_ID)) ||
    '';
  return String(raw || '').trim();
}

/**
 * Best-effort removal of GTM tags after consent withdrawal.
 */
export function unloadGoogleTagManager() {
  if (typeof document === 'undefined') return;
  const script = document.getElementById('ct-gtm-script');
  if (script) script.remove();
  const noscript = document.getElementById('ct-gtm-noscript');
  if (noscript) noscript.remove();
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'ct_consent_withdrawn' });
  }
}

/**
 * @param {string} [gtmId]
 * @returns {boolean}
 */
export function loadGoogleTagManager(gtmId = resolveGtmId()) {
  const id = String(gtmId || '').trim();
  if (!id || typeof document === 'undefined') return false;
  if (document.getElementById('ct-gtm-script')) return true;

  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
  }

  const script = document.createElement('script');
  script.id = 'ct-gtm-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  if (!document.getElementById('ct-gtm-noscript')) {
    const noscript = document.createElement('noscript');
    noscript.id = 'ct-gtm-noscript';
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    iframe.title = 'Google Tag Manager';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }
  return true;
}

export function enableAnalytics(source = 'banner') {
  setStoredConsent(CONSENT_ALL, source);
  loadGoogleTagManager();
  if (typeof window !== 'undefined' && typeof window.trackEvent === 'function') {
    window.trackEvent('consent_analytics_granted', { consent: CONSENT_ALL });
  }
}

export function enableNecessaryOnly(source = 'banner') {
  setStoredConsent(CONSENT_NECESSARY, source);
  unloadGoogleTagManager();
}

/** Withdraw optional analytics and keep necessary-only evidence. */
export function withdrawAnalyticsConsent(source = 'manage') {
  enableNecessaryOnly(source);
}

export function initAnalyticsFromStoredConsent() {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
  if (getStoredConsent() === CONSENT_ALL) {
    loadGoogleTagManager();
  } else {
    unloadGoogleTagManager();
  }
}

/** Open manage UI via custom event (banner / footer). */
export function openCookiePreferences() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('ct-open-cookie-preferences'));
}
